import { useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import {
  emptyLabelTrash,
  GPanelContactsKeys,
} from '../../../gpanel-contacts-api';
import { useStore } from '../../../../../state/zustand-store';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { Messages } from '../../../../../../utils/message-constants';
import SharedDialog from '../../../../../../shared/shared-dialog';

import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

export default function GpanelContactsEmptyLabelsTrashDialog({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () => emptyLabelTrash(changeLogMessage),
    onSuccess: () => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Emptied(`Labels trash`),
      });
      queryClient.invalidateQueries({
        queryKey: [GPanelContactsKeys.TrashLabels],
      });
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              Messages.Errors.Network.Emptying(`the labels trash`),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.empty_label_trash, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.EmptyTrashedLabels.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title="Empty Trash"
        dialogContent={
          <>
            <Typography>
              Labels in Trash will be deleted forever. This can't be undone.
            </Typography>
            {errorText && <FormHelperText error children={errorText} />}
          </>
        }
        primaryButton={{
          title: 'Empty Trash',
          color: 'error',
          action: handleSubmit,
          loading: isPending,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
